import {collectValues} from '@/utils/collect';
import {minDate, toDate} from '@/utils/dates';
import {byRef} from '@/utils/firestore';
import * as impl from '@/room-booking/v1/bookings';

/**
 * @typedef {Object} Booking
 * @property {string} id
 * @property {firebase.firestore.DocumentReference} ref
 * @property {string} title
 * @property {Object} owner
 * @property {firebase.firestore.DocumentReference} owner.ref
 * @property {string} owner.title
 * @property {Object} space
 * @property {firebase.firestore.DocumentReference} space.ref
 * @property {string} space.title
 * @property {CanBeDate} fromTime
 * @property {CanBeDate} toTime
 * @property {Object} [checkIn]
 * @property {CanBeDate} checkIn.fromTime
 * @property {CanBeDate} checkIn.toTime
 * @property {Object} [connection]
 * @property {firebase.firestore.DocumentReference} connection.ref
 * @property {number} [connectionCount]
 * @property {boolean} [requiresCheckIn]
 * @property {Object<string,(boolean|string)>|boolean} [privacy]
 */

/**
 * Get the next booking to start after the given time for the given resource.
 *
 * @param {Booking[]} bookings
 * @param {function(b:Booking):string} reservationKeyFn
 * @param {CanBeDate} now
 * @return {Booking|null}
 */
export function nextBooking(bookings, reservationKeyFn, now) {
  const index = nextBookingIndex(bookings, reservationKeyFn, now);
  return index === -1 ? null : bookings[index];
}

/**
 * Get the index of the next booking to start after the given time
 * @param {Booking[]} bookings
 * @param {function(b:Booking):string} reservationKeyFn
 * @param {CanBeDate} now
 * @return {number} -1 if no such booking
 */
export function nextBookingIndex(bookings, reservationKeyFn, now) {
  now = toDate(now);
  let index = -1;
  let closestTime = null;
  for (let i = 0; i < bookings.length; i++) {
    const b = bookings[i];
    const reservationKey = reservationKeyFn(b);
    const bStart = bookingStart(b, reservationKey);
    if (bStart >= now) {
      if (index === -1 || closestTime > bStart) {
        index = i;
        closestTime = bStart;
      }
    }
  }
  return index;
}

/**
 * Sorts the given bookings by their start time. Earliest to start first.
 *
 * @param {Booking[]} bookings
 * @param {function(b:Booking):string} reservationKeyFn
 * @return {Booking[]}
 */
export function inStartOrder(bookings, reservationKeyFn) {
  return bookings.sort((b1, b2) => {
    const t1 = bookingStart(b1, reservationKeyFn(b1));
    const t2 = bookingStart(b2, reservationKeyFn(b2));
    if (t1 === t2) return 0;
    if (t1 < t2) return -1;
    return 1;
  });
}

/**
 * Sorts the given bookings by their end time. Earliest to finish first.
 *
 * @param {Booking[]} bookings
 * @param {function(b:Booking):string} reservationKeyFn
 * @return {Booking[]}
 */
export function inEndOrder(bookings, reservationKeyFn) {
  return bookings.sort((b1, b2) => {
    const t1 = bookingEnd(b1, reservationKeyFn(b1));
    const t2 = bookingEnd(b2, reservationKeyFn(b2));
    if (t1 === t2) return 0;
    if (t1 < t2) return -1;
    return 1;
  });
}

/**
 * Returns a booking from bookings that is 'closest' to now. Closest is defined as the first matching booking from
 * 1. start <= now < end : the first active booking
 * 2. min(start > now) : the next booking to occur
 * 3. max(end <= now) : the last booking to finish
 *
 * @param {Booking|Booking[]} bookings
 * @param {function(b:Booking):string} reservationKeyFn
 * @param {CanBeDate} now
 * @return {Booking|null}
 */
export function closestBooking(bookings, reservationKeyFn, now) {
  if (!Array.isArray(bookings)) return bookings;
  if (bookings.length === 0) return null;
  if (bookings.length === 1) return bookings[0];

  now = toDate(now);

  // find the most relevant booking, priority being
  // 1. The first active bookings we find
  // 2. The next booking to start
  // 3. The last booking to finish
  const chosen = {
    booking: null,
    past: false,
    future: false,
    from: null,
    to: null,
    choose(booking, from, to, reservationKey) {
      this.booking = booking;
      if (from === undefined) from = toDate(impl.fromTime(booking, reservationKey));
      if (to === undefined) to = toDate(impl.toTime(booking, reservationKey));
      this.from = from;
      this.to = to;
      this.future = from > now;
      this.past = to <= now;
    }
  };
  for (const b of bookings) {
    const reservationKey = reservationKeyFn(b);
    const from = bookingStart(b, reservationKey);
    const to = bookingEnd(b, reservationKey);

    const futureBooking = from > now;
    const pastBooking = to <= now;

    // check if the booking is active
    if (!futureBooking && !pastBooking) {
      return b;
    }
    // first booking we've seen, use it if there are no more
    if (chosen.booking === null) {
      chosen.choose(b, from, to, reservationKey);
      continue;
    }

    if (futureBooking) {
      if (!chosen.future) {
        // the first future booking we've seen
        chosen.choose(b, from, to, reservationKey);
      } else if (from < chosen.from) {
        // an earlier future booking than those we've seen
        chosen.choose(b, from, to, reservationKey);
      }
    } else if (!chosen.future) {
      // don't replace future bookings with past ones
      if (to > chosen.to) {
        // this booking completed closer to now
        chosen.choose(b, from, to, reservationKey);
      }
    }
  }

  return chosen.booking;
}

/**
 * Return the root of a connection path starting with booking.
 * @param {Object<string,Booking>|function(string):Booking} bookingsByRef
 * @param {Booking|null} booking
 * @return {Booking|null}
 */
export function rootBooking(bookingsByRef, booking) {
  if (!booking) return booking;
  if (typeof bookingsByRef !== 'function') {
    const dict = bookingsByRef;
    bookingsByRef = ref => dict[ref];
  }
  while (booking.connection) {
    const connectedTo = bookingsByRef(impl.connectionRefKey(booking));
    if (connectedTo) {
      booking = connectedTo;
    } else {
      break;
    }
  }
  return booking;
}

/**
 * Checks if now is during a booking's active time
 * @param {Booking} booking
 * @param {string} reservationKey
 * @param {Date} now
 * @return {boolean}
 */
export function isActive(booking, reservationKey, now) {
  const bStart = bookingStart(booking, reservationKey);
  const bEnd = bookingEnd(booking, reservationKey);
  return bStart <= now && now <= bEnd;
}

/**
 * Checks if now is between the from and to times of the booking.
 * @param {Booking} booking
 * @param {string} reservationKey
 * @param {Date} now
 * @return {boolean}
 */
export function isBooked(booking, reservationKey, now) {
  const bStart = toDate(impl.fromTime(booking, reservationKey));
  const bEnd = toDate(impl.toTime(booking, reservationKey));
  return bStart <= now && now <= bEnd;
}

/**
 * Returns the start of the booking, the earliest of fromTime and checkIn time.
 * @param {Booking} booking
 * @param {string} reservationKey
 * @return {Date}
 */
export function bookingStart(booking, reservationKey) {
  return minDate(impl.fromTime(booking, reservationKey),
      ...collectValues(booking, impl.checkInFromProp(reservationKey)));
}

/**
 * Returns the end of the booking, the earliest of toTime and checkIn end time.
 * @param {Booking} booking
 * @param {string} reservationKey
 * @return {Date}
 */
export function bookingEnd(booking, reservationKey) {
  return minDate(impl.toTime(booking, reservationKey),
      ...collectValues(booking, impl.checkInToProp(reservationKey)));
}

/**
 * Returns all bookings that say they are connected to the given one, recursively.
 *
 * @param {Booking} booking
 * @param {Booking[]} knownBookings
 * @return {Booking[]}
 */
export function connectedBookings(booking, knownBookings) {
  if (!impl.connectionCount(booking)) return [];

  // keyed by the ref.path of the connection, this contains the reverse references for booking connections
  const connectedTo = {};
  for (let i = 0; i < knownBookings.length; i++) {
    const b = knownBookings[i];
    const connectionRef = impl.connectionRefKey(b);
    if (connectionRef) {
      const bucket = connectedTo[connectionRef] || (connectedTo[connectionRef] = []);
      bucket.push(b);
    }
  }

  const result = [];
  const seen = new Set();
  const searchRefs = [byRef(booking)];
  while (searchRefs.length > 0) {
    const ref = searchRefs.pop();
    const connectsTo = connectedTo[ref];
    if (connectsTo) {
      for (let i = 0; i < connectsTo.length; i++) {
        const k = byRef(connectsTo[i]);
        if (!seen.has(k)) {
          seen.add(k);
          result.push(connectsTo[i]);
          searchRefs.push(k);
        }
      }
    }
  }

  return result;
}
